import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { type OrgListItem } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableId } from "../components/CopyableId";
import { OrgDetailPanel } from "../components/OrgDetailPanel";

const PAGE_SIZE = 25;

export function OrganizationsPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "organizations", label: "Organizations", active: true, onSelect: () => navigate("/support/organizations") },
    { id: "invitations", label: "Invitations", active: false, onSelect: () => navigate("/support/organization-invitations") },
    { id: "create", label: "Create", active: false, onSelect: () => navigate("/support/create-organization") },
  ]);

  // Selection + filter live in the URL so browser back/forward works and the org panel is
  // restorable after drilling into the members list on the Users page.
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const selectedOrg = params.get("org");

  const [input, setInput] = useState(q);
  const [skip, setSkip] = useState(0);
  const [items, setItems] = useState<OrgListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the box in sync, and reset to the first page, when the filter changes (incl. back/forward).
  useEffect(() => { setInput(q); setSkip(0); }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.listOrganizations({ filter: q, skip, take: PAGE_SIZE }).then(
      (page) => { if (!cancelled) { setItems(page.items); setTotal(page.total); } },
      (e) => { if (!cancelled) { setError(`${e}`); setItems([]); setTotal(0); } },
    ).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, q, skip]);

  function submit(e: FormEvent) {
    e.preventDefault();
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      const v = input.trim();
      if (v) next.set("q", v); else next.delete("q");
      next.delete("org");
      return next;
    });
  }

  function selectOrg(id: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("org", id);
      return next;
    });
    queueMicrotask(() => {
      document.getElementById("org-detail-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function closePanel() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("org");
      return next;
    });
  }

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE_SIZE, total);

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Organizations</div>
        <h1>Organizations</h1>
        <p>
          Browse and search organizations. Click one to see its licence — seats and entitlements — and
          jump to its members.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Find organization
          <span className="hint">name or id — leave blank to list all</span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={submit}>
            <input
              type="search"
              placeholder="Acme, or an organization id"
              spellCheck={false}
              autoComplete="off"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button className={"btn btn-primary" + (loading ? " loading" : "")}>
              <span className="glyph" /> Search
            </button>
          </form>

          {error && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{error}</span>
            </div>
          )}

          {items && (
            <div style={{ marginTop: 18, border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Organization id</th>
                    <th>Created</th>
                    <th className="actions">Members</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={4}>
                        no organizations {q ? <>matched <span className="mono">{q}</span></> : "found"}
                      </td>
                    </tr>
                  )}
                  {items.map((o) => (
                    <tr
                      key={o.id}
                      className={selectedOrg === o.id ? "row-selected" : undefined}
                      style={{ cursor: "pointer" }}
                      onClick={() => selectOrg(o.id)}
                    >
                      <td>{o.name ?? <span className="faint">(unnamed)</span>}</td>
                      <td><CopyableId value={o.id} /></td>
                      <td className="mono">{fmtDate(o.createdDateTimeUtc)}</td>
                      <td className="actions mono">{o.memberCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="hint" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px" }}>
                <span>{from}–{to} of {total}</span>
                <span style={{ flex: 1 }} />
                <button className="btn btn-ghost btn-sm" disabled={skip === 0 || loading} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}>
                  Prev
                </button>
                <button className="btn btn-ghost btn-sm" disabled={to >= total || loading} onClick={() => setSkip(skip + PAGE_SIZE)}>
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {selectedOrg && <OrgDetailPanel orgId={selectedOrg} onClose={closePanel} />}
    </>
  );
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
